import {ReactElement, useMemo, useRef, useState} from "react";
import {IMetricIssue, IMetricSolverReturn, solveMoveIns} from "./logic/useMetricIssues";
import {Button} from "@zendeskgarden/react-buttons";
import {Check, ChevronLeft, ChevronRight, Warning} from "@material-ui/icons";
import * as css from "./styles/metric-issues.module.scss";
import {TooltipModal} from "@zendeskgarden/react-modals";
import {Property} from "../../../../../../contexts/properties/logic";
import {getMonths} from "../../../../../../utils/months-helper";
import { AvailableMetrics, metricToLabelMap } from '../CombinedMetricsTable';
import {OriginalRevenueOperationalMetricValidKey} from "../../../../../../__generated__/generated_types";
import {round} from "../../../../../../utils/math";
import {ImMagicWand} from "react-icons/im";
import {rateToCount} from "../../logic/combined-metrics-logic";
import { ReactComponent as VZIconUnitType } from "../../../../../../assets/icons/vzicon_unit_type.svg";
import {Hint} from "@zendeskgarden/react-forms";


export interface MetricIssuesProps {
    property: Property;
    issues: IMetricIssue[];
    onChange: (
        monthIndex: number,
        unitTypeName: string,
        rate: number | null,
        metric: AvailableMetrics,
    ) => void;
}

export default function MetricIssues({property, issues, onChange}: MetricIssuesProps): ReactElement {
    const months = getMonths(property);
    const buttonRef = useRef<HTMLButtonElement>(null);

    const [isOpen, setIsOpen] = useState(false);
    const [currentIssueIndex, setCurrentIssueIndex] = useState<number>(0);

    const currentIssue = useMemo<IMetricIssue | null>(() => {
        let issue = issues[currentIssueIndex] ?? null;
        if (issue === null && issues.length > 0) {
            setCurrentIssueIndex(0);
            return null;
        }
        return issue;
    }, [issues, currentIssueIndex]);

    const {occupancySuggestion, renewalSuggestion} = useMemo<IMetricSolverReturn>(() => {
        if (!currentIssue) {
            return {
                occupancySuggestion: null,
                renewalSuggestion: null,
            }
        }
        const solutions = solveMoveIns(
            currentIssue.counts.leaseExpirationCount[currentIssue.monthIndex] ?? 0,
            currentIssue.counts.renewalRatio[currentIssue.monthIndex] ?? 0,
            currentIssue.counts.occupancyRate[currentIssue.monthIndex] ?? 0,
            currentIssue.monthIndex === 0 ? rateToCount(currentIssue.previousVersionLastMonthData.occupancyRate, currentIssue.unitTypeCount) : currentIssue.counts.occupancyRate[currentIssue.monthIndex - 1] ?? 0,
            currentIssue.unitTypeCount,
        );
        return {
            occupancySuggestion: solutions.occupancySuggestion,
            renewalSuggestion: solutions.renewalSuggestion,
        }
    }, [currentIssue]);

    const renewalsImpossible = useMemo(() => {
        return renewalSuggestion === null || renewalSuggestion < 0 || renewalSuggestion > 100;
    }, [renewalSuggestion]);

    const issueDescription = useMemo<string>(() => {
        let text = "";
        if (renewalsImpossible) {
            text = "You don't have enough Expirations to give you the Move Outs needed. You can click the button next to Occupancy to quickly set it.";
        } else {
            text = "You can either lower your Renewals or increase your Occupancy. Click one of the buttons below to quickly set it.";
        }
        return text;
    }, [renewalsImpossible]);

    function renderIssueRow(metricKey: AvailableMetrics) {
        if (!currentIssue) {
            return <></>;
        }
        let metricValue = currentIssue.rates[metricKey][currentIssue.monthIndex] ?? 0;
        // we need to display the move in count NOT rounded
        if (metricKey !== OriginalRevenueOperationalMetricValidKey.MoveInCount) {
            metricValue = round(metricValue, 1);
        }
        let prevMonthName = months[currentIssue.monthIndex - 1];
        let prevMonthMetricValue = round(currentIssue.rates[metricKey][currentIssue.monthIndex - 1] ?? 0, 1);

        if (currentIssue.monthIndex === 0) {
            prevMonthName = months[11];
            prevMonthMetricValue = round(currentIssue.previousVersionLastMonthData[metricKey] ?? 0, 1);
        }

        const showPercentSymbol = [
            OriginalRevenueOperationalMetricValidKey.RenewalRatio,
            OriginalRevenueOperationalMetricValidKey.MoveOutRate,
            OriginalRevenueOperationalMetricValidKey.OccupancyRate,
        ].includes(metricKey);

        return (
            <tr>
                <td>{metricToLabelMap.get(metricKey)}</td>
                <td className={css.prevMonth}>{prevMonthMetricValue}{showPercentSymbol ? "%" : ""}</td>
                <td >{metricValue}{showPercentSymbol ? "%" : ""}</td>
                <td>
                    {metricKey === OriginalRevenueOperationalMetricValidKey.RenewalRatio &&
                        <Button
                            disabled={renewalsImpossible}
                            size="small"
                            onClick={() => onChange(currentIssue.monthIndex, currentIssue.unitTypeName, renewalSuggestion, OriginalRevenueOperationalMetricValidKey.RenewalRatio)}
                        >
                            {renewalsImpossible ?
                                "N/A"
                                :
                                <>
                                    <Button.StartIcon><ImMagicWand /></Button.StartIcon>
                                    {renewalSuggestion}%
                                </>
                            }
                        </Button>
                    }
                    {metricKey === OriginalRevenueOperationalMetricValidKey.OccupancyRate &&
                        <Button
                            size="small"
                            onClick={() => onChange(currentIssue.monthIndex, currentIssue.unitTypeName, occupancySuggestion, OriginalRevenueOperationalMetricValidKey.OccupancyRate)}
                        >
                            <Button.StartIcon><ImMagicWand /></Button.StartIcon>
                            {occupancySuggestion}%
                        </Button>
                    }
                </td>
            </tr>
        );
    }

    return (
        <div className={css.metricIssues}>
            <Button
                ref={buttonRef}
                isBasic
                size="small"
                className={`${css.buttonTrigger} ${issues.length > 0 && css.warning}`}
                onClick={() => setIsOpen(true)}
            >
                <Button.StartIcon>
                    {issues.length > 0 ? <Warning /> : <Check />}
                </Button.StartIcon>
                {issues.length === 0 ?
                    "No Issues"
                    :
                    `${issues.length} ${issues.length === 1 ? "Issue" : "Issues"}`
                }
            </Button>

            {isOpen && buttonRef.current && currentIssue &&
                <TooltipModal
                    referenceElement={buttonRef.current}
                    onClose={() => setIsOpen(false)}
                    placement="end-top"
                    className={css.modal}
                >
                    <TooltipModal.Title>Negative Move In ({currentIssueIndex + 1} of {issues.length})</TooltipModal.Title>
                    <TooltipModal.Body
                        className={css.body}
                        // key usage here gives us the issue change animation by forcing it to remount the modal body
                        key={`${currentIssue.unitTypeName}-${currentIssue.monthIndex}`}
                    >
                        <h5 className={css.greyHeader}>
                            <span>
                                <VZIconUnitType />
                                {currentIssue?.unitTypeName}
                            </span>
                            <span>
                                {months[currentIssue.monthIndex]}.
                            </span>
                        </h5>
                        <p className={css.highlightSection}>
                            <span>THE FIX</span>
                            <br></br>
                            {issueDescription}
                        </p>
                        
                        <table className={css.table}>
                            <thead>
                                <tr>
                                    <th></th>
                                    <th className={css.prevMonth}>{months[currentIssue.monthIndex === 0 ? 11 : currentIssue.monthIndex - 1]}</th>
                                    <th>{months[currentIssue.monthIndex]}</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {Object.keys(currentIssue.counts).map(metricKey =>
                                    renderIssueRow(metricKey as AvailableMetrics)
                                )}
                            </tbody>
                        </table>
                        <Hint className={css.hintText}>Solving one issue may introduce another later in the year. That's normal, and we'll automatically add any new ones to this list.</Hint>
                    </TooltipModal.Body>

                    <TooltipModal.Footer className={css.footer}>
                        <TooltipModal.FooterItem>
                            <Button
                                isPrimary
                                size="small"
                                disabled={currentIssueIndex <= 0}
                                onClick={() => setCurrentIssueIndex(currentIssueIndex - 1)}
                            >
                                <Button.StartIcon><ChevronLeft /></Button.StartIcon>
                                Previous
                            </Button>
                        </TooltipModal.FooterItem>
                        <TooltipModal.FooterItem>
                            <Button
                                isPrimary
                                size="small"
                                disabled={currentIssueIndex >= issues.length - 1}
                                onClick={() => setCurrentIssueIndex(currentIssueIndex + 1)}
                            >
                                Next
                                <Button.EndIcon><ChevronRight /></Button.EndIcon>
                            </Button>
                        </TooltipModal.FooterItem>
                    </TooltipModal.Footer>
                    <TooltipModal.Close aria-label="Close" />
                </TooltipModal>
            }
        </div>
    );
}
